import { v4 as uuidv4 } from 'uuid';
import authService from './authService';
import { API_BASE_URL } from '../config';

// Extract host from API_BASE_URL
const getApiHost = () => {
  try {
    const url = new URL(API_BASE_URL);
    return url.host;
  } catch {
    // Fallback to hardcoded for production
    return 'api.sh.co.ke';
  }
};

export interface WebSocketMessage {
  type: string;
  message_id?: number;
  contact_id?: number;
  message_type?: string;
  timestamp?: string;
  has_media?: boolean;
  status?: string;
  wa_message_id?: string;
  offline_sync?: boolean;
}

export class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: ((data: WebSocketMessage) => void)[] = [];
  private clientId: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectDelay: number = 1000;
  private lastMessageTimestamp: string | null = null;
  private isConnecting: boolean = false;
  private authToken: string | null = null;
  
  constructor() {
    // Generate a unique client ID
    this.clientId = uuidv4();
    
    // Load last message timestamp from localStorage
    this.lastMessageTimestamp = localStorage.getItem('lastMessageTimestamp');
  }
  
  connect(token: string) {
    // Prevent multiple connection attempts
    if (this.socket || this.isConnecting) return;
    
    this.isConnecting = true;
    
    // Store the token for reconnection
    this.authToken = token;
    
    // Create new WebSocket connection with auth token and client ID
    // Use the correct API domain for WebSocket connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Get API host from API_BASE_URL
    const apiHost = getApiHost();

    // Make sure we're using the correct API path
    const wsUrl = `${protocol}//${apiHost}/api/v1/ws?client_id=${this.clientId}`;
    
    console.log(`Connecting to WebSocket at ${wsUrl}`);
    
    // Create WebSocket with authorization header
    this.socket = new WebSocket(wsUrl);
    
    // Set the authorization header before the connection is established
    this.socket.onopen = () => {
      // Send the authorization header as the first message
      if (this.socket) {
        this.socket.send(JSON.stringify({ type: 'authorization', token: `Bearer ${token}` }));
      
        console.log('WebSocket connection established, sent authorization');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        
        // Perform offline sync after connection
        this.performOfflineSync();
      }
    };
    
    // onopen handler is set above
    
    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;
        console.log('WebSocket message received:', data);
        
        // Update last message timestamp
        if (data.timestamp) {
          this.lastMessageTimestamp = data.timestamp;
          localStorage.setItem('lastMessageTimestamp', data.timestamp);
        }
        
        // Notify all handlers
        this.messageHandlers.forEach(handler => handler(data));
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    this.socket.onclose = (event) => {
      console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
      this.socket = null;
      this.isConnecting = false;
      
      // Attempt to reconnect with exponential backoff
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
        console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        setTimeout(() => this.connect(token), delay);
      } else {
        console.error('Maximum reconnection attempts reached');
      }
    };
    
    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }
  
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
  
  addMessageHandler(handler: (data: WebSocketMessage) => void) {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }
  
  async performOfflineSync() {
    if (!this.lastMessageTimestamp) return;
    
    try {
      console.log(`Performing offline sync since ${this.lastMessageTimestamp}`);
      
      // Fetch messages since last received timestamp
      const token = authService.getToken();
      const response = await fetch(`${API_BASE_URL}/messages/since/${this.lastMessageTimestamp}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const messages = await response.json();
      console.log(`Received ${messages.length} messages during offline sync`);
      
      // Process missed messages
      messages.forEach((message: any) => {
        this.messageHandlers.forEach(handler => handler({
          type: 'new_message',
          message_id: message.id,
          contact_id: message.contact_id,
          message_type: message.message_type,
          timestamp: message.timestamp,
          has_media: !!message.media_url,
          offline_sync: true
        }));
      });
    } catch (error) {
      console.error('Error performing offline sync:', error);
    }
  }
  
  // Helper method to check if connected
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }
}

// Create a singleton instance
export const websocketService = new WebSocketService();
